import classNames from 'classnames'
import arrayMutators from 'final-form-arrays'
import i18next from 'i18next'
import { Form } from 'react-final-form'
import { FieldArray } from 'react-final-form-arrays'
import { Trans, useTranslation } from 'react-i18next'
import { AddEntityFieldButton } from '../../../../common/components/Form/AddEntityFieldButton/AddEntityFieldButton'
import { InputFormField } from '../../../../common/components/Form/InputFormField/InputFormField'
import { Button } from '../../../../common/components/button/Button'
import { useDisableBodyScroll } from '../../../../hooks/useDisableBodyScroll'
import { DeepPartial, FormErrorType } from '../../../../types/commonTypes'
import { safeIsNullOrEmpty } from '../../../../util/string'
import styles from './AddMembersModal.module.css'
import Papa from 'papaparse'
import { useCallback, useState } from 'react'
import { ShowAllMembersButton } from '../../../../common/components/Form/ShowAllMembersButton/ShowAllMembersButton'
import { useNotification } from '../../../../contexts/NotificationContext'
import { AddMemberError } from '../../../../common/components/Form/AddMemberError/AddMemberError'
import { emailValidation } from '../../../../util/validate'
import { getIcon } from '../../../../common/components/icons/utils'
import { AddPlayersFormValues, Player } from './AddPlayersModal'
import { PopupActionMenu, PopupActionMenuItem } from '../../../../common/components/PopupMenu/PopupActionMenu'
import { csvFileInfo } from '../../../../contexts/BusinessSettingsContext'
import { CellElement, Table, TableRow } from '../../../reports/components/GenericTable'
import { SelectFormField } from '../../../../common/components/Form/SelectFormField/SelectFormField'
import { SelectVariant } from '../../../../common/components/Select/Select'
import { useUser } from '../../../../contexts/userContext'

type AddPlayersProps = {
  onPreview: () => void
  onClose: () => void
  values: AddPlayersFormValues | null
  setAllValues: (values: AddPlayersFormValues) => void
  setCSVValues: (file: csvFileInfo) => Promise<any>
  playerCount: number
  maxPlayers: number
  limitPlayers: boolean
}

const initialValues: { players: Player[] } = {
  players: [{ name: '', email: '', tag: 0 }],
}

export const AddPlayers: React.FC<AddPlayersProps> = ({
  onClose,
  onPreview,
  values,
  setAllValues,
  setCSVValues,
  playerCount,
  maxPlayers,
  limitPlayers,
}) => {
  const { t } = useTranslation()
  useDisableBodyScroll()
  const [showAllPlayers, setShowAllPlayers] = useState(true)
  const { notifyError } = useNotification()
  const [updatedPlayerCount, setUpdatedPlayerCount] = useState(playerCount)
  const [hasCSVData, setHasCSVData] = useState(false)
  const [players, setPlayers] = useState<Player[] | null>(null)
  const { user } = useUser()
  const [allowSubmitting, setAllowSubmitting] = useState(false)

  const validate = (values: DeepPartial<AddPlayersFormValues>): FormErrorType<AddPlayersFormValues> => {
    const returnablePlayers = (values.players || []).map((player) => {
      const name = hasCSVData
        ? undefined
        : safeIsNullOrEmpty(player?.name)
        ? i18next.t('validation_errors.required', 'Required')
        : undefined
      const email = hasCSVData ? undefined : emailValidation(player?.email)
      if ((name || email) && hasCSVData) {
        setShowAllPlayers(true)
      }
      return {
        name: name,
        email: email,
      }
    })
    return { players: returnablePlayers }
  }

  const validateAndAddCSVData = useCallback(
    async (data: any, file: any) => {
      const playerKeys = ['name', 'email', 'tag']
      if (data.length > 9) {
        setShowAllPlayers(true)
      }
      setUpdatedPlayerCount(playerCount - 1)
      if (data instanceof Array) {
        const hasKeys = data.every((player, index) => {
          if (playerKeys.every((playerKey) => player.hasOwnProperty(playerKey))) {
            const newPlayer: Player = {
              name: player.name,
              email: player.email,
              tag: player.tag.trim(),
            }
            setUpdatedPlayerCount((prev) => prev + 1)
            setPlayers((prev) => (prev ? [...prev, newPlayer] : [newPlayer]))
            return true
          }

          notifyError({
            title: t(
              'settings_organization.players.add_players.errors.downloading_csv.title',
              'Error downloading .csv file',
            ),
            content: t(
              'settings_organization.players.add_players.errors.downloading_csv.content',
              'Error on line %{line}',
              { line: index + 2 },
            ),
          })
          return false
        })
        if (hasKeys) {
          const response = await setCSVValues({ fileName: file.name, inputFile: file })
          if (response.success) {
            setHasCSVData(true)
            setAllowSubmitting(true)
          }
          return true
        }
        return false
      }
      notifyError({
        title: t(
          'settings_organization.players.add_players.errors.downloading_csv.title',
          'Error downloading .csv file',
        ),
        content: t(
          'settings_organization.players.add_players.errors.downloading_csv_no_headers.content',
          'Something went wrong when adding .csv file. This is most likely because some data in the file was not what we expected.',
        ),
      })
      return false
    },
    [playerCount, notifyError, t, setCSVValues],
  )

  const headerTransform = (header: string) => {
    return header.toLocaleLowerCase().replace('-', '')
  }

  const parsePlayerCSV = useCallback(
    (data: any): Player[] | void => {
      const parsed = Papa.parse<Player>(data, {
        header: true,
        dynamicTyping: true,
        skipEmptyLines: 'greedy',
        transformHeader: headerTransform,
        complete: (results) => {
          if (results.errors.length) {
            notifyError({
              title: t('settings_organization.players.add_players.errors.parsing_csv.title', 'Error parsing .csv file'),
              content:
                t(
                  'settings_organization.players.add_players.errors.parsing_csv.content',
                  'Could not parse the .csv file',
                ) +
                '\n' +
                results.errors[0].message +
                ', row: ' +
                results.errors[0].row,
            })
          } else if (results.data) {
            validateAndAddCSVData(results.data, data)
          } else {
            notifyError({
              title: t('settings_organization.players.add_players.errors.parsing_csv.title', 'Error parsing .csv file'),
              content: t(
                'settings_organization.players.add_players.errors.parsing_csv.content',
                'Could not parse the .csv file',
              ),
            })
          }
        },
      })
      return parsed
    },
    [notifyError, t, validateAndAddCSVData],
  )

  const onAddCSV = useCallback(
    (e: any) => {
      const file = e.target.files?.[0]
      if (!file) return
      setAllowSubmitting(false)
      parsePlayerCSV(file)
    },
    [parsePlayerCSV],
  )

  const handleSetAllValues = useCallback(
    (values: AddPlayersFormValues) => {
      const newValues = {
        players: values.players,
      }
      setAllValues(newValues)
    },
    [setAllValues],
  )

  const onPreviewInternal = (values: AddPlayersFormValues) => {
    handleSetAllValues(values)
    onPreview()
  }

  const handleDeleteRows = useCallback((rows: number | undefined) => {
    setUpdatedPlayerCount((prev) => prev - 1)
    if (rows !== undefined && rows < 9) {
      setShowAllPlayers(true)
    }
  }, [])

  const createTableRowData = useCallback(() => {
    const rows: TableRow[] = []
    players?.forEach((dataRow) => {
      const row = dataRow as Player
      const tableRow: CellElement[] = [row.name, row.email, row.tag || row.tag || '']
      rows.push(tableRow)
    })
    return rows
  }, [players])

  const tableRowData = createTableRowData()

  const getTagOptions = () => {
    const options = user?.tags.map((tag) => {
      return { value: tag.id, label: tag.label }
    })
    if (options) {
      options.unshift({ value: 0, label: t('settings_organization.players.add_players.no_tag', 'No tag') })
    }
    return options || []
  }

  return (
    <>
      <div className={classNames(styles.description, 'medium')}>
        <Trans i18nKey='settings_organization.players.add_players.description' components={{ 1: <br /> }}>
          {
            'Adding player accounts allows players to log in and play multiple games with one user licence. <br/> <br/>When uploading an Excel or CSV file, ensure the first column has player names, the second has e-mail, and the third contains a optional tag you want the player to have. You can only add one tag / player when creating the player.'
          }
        </Trans>
        <div style={{ marginTop: '20px' }}>
          {t('settings_organization.players.add_players.CSV_example', 'Example of correct CSV file:')}
        </div>
        <table className={styles.table}>
          <thead>
            <tr className={classNames(styles.tr, styles.trFirst)}>
              <th className={classNames(styles.th, styles.thLT)}>Name</th>
              <th className={styles.th}>Email</th>
              <th className={classNames(styles.th, styles.thRT)}>Tag</th>
            </tr>
          </thead>
          <tbody>
            <tr className={styles.tr}>
              <td className={styles.td}> </td>
              <td className={styles.td}> </td>
              <td className={styles.td}> </td>
            </tr>
            <tr className={styles.tr}>
              <td className={styles.td}> </td>
              <td className={styles.td}> </td>
              <td className={styles.td}> </td>
            </tr>
            <tr className={classNames(styles.tr, styles.trLast)}>
              <td className={classNames(styles.td, styles.tdLB)}> </td>
              <td className={styles.td}> </td>
              <td className={classNames(styles.td, styles.tdRB)}> </td>
            </tr>
          </tbody>
        </table>
      </div>
      <div className={styles.csvButtonContainer}>
        <form>
          <label className={styles.csvButton}>
            <input type={'file'} id={'csvFileInput'} accept={'.csv'} onChange={onAddCSV} style={{ display: 'none' }} />
            {getIcon('textFile')}
            {t('settings_organization.players.add_players.button_text.upload_csv', 'Upload CSV')}
          </label>
        </form>
      </div>

      <Form<AddPlayersFormValues>
        onSubmit={onPreviewInternal}
        validate={validate}
        mutators={{
          ...arrayMutators,
        }}
        initialValues={values ? values : initialValues}
      >
        {({ handleSubmit, form, submitting, invalid }) => (
          <form onSubmit={handleSubmit}>
            <div className={styles.mainFormContainer}>
              <div className={styles.rowsContainer}>
                {!hasCSVData ? (
                  <FieldArray name='players'>
                    {({ fields }) =>
                      fields.map((name, index) => (
                        <div
                          key={`player_${index}`}
                          className={classNames(styles.addMemberRow, index > 9 && !showAllPlayers && styles.extraRow)}
                        >
                          <InputFormField
                            name={`${name}.name`}
                            label={t('settings_organization.players.add_players.labels.name', 'Name')}
                            placeholder={t('settings_organization.players.add_players.placeholders.name', 'Type name')}
                            alwaysShowErrors={hasCSVData}
                          />
                          <InputFormField
                            name={`${name}.email`}
                            type='email'
                            label={t('settings_organization.players.add_players.labels.email', 'Email')}
                            placeholder={t(
                              'settings_organization.players.add_players.placeholders.email',
                              'Enter E-mail',
                            )}
                            alwaysShowErrors={hasCSVData}
                          />
                          <div className={styles.lastColumn}>
                            <SelectFormField
                              name={`${name}.tag`}
                              label={t('settings_organization.players.add_players.labels.tag', 'Tag (optional)')}
                              options={getTagOptions()}
                              variant={SelectVariant.XLarge}
                            />
                          </div>
                          {fields?.length && fields.length > 1 && (
                            <div className={styles.actionMenu}>
                              <PopupActionMenu id='add-members-action-menu'>
                                <PopupActionMenuItem
                                  icon='trash'
                                  text={t('common.delete', 'Delete')}
                                  onClick={() => {
                                    fields.remove(index)
                                    handleDeleteRows(fields?.length)
                                  }}
                                />
                              </PopupActionMenu>
                            </div>
                          )}
                        </div>
                      ))
                    }
                  </FieldArray>
                ) : (
                  <Table
                    headerWidths={['40%', '60%', 'fit-content']}
                    headerLabels={[
                      t('settings_organization.player_accounts.table_headers.name', 'Name'),
                      t('settings_organization.player_accounts.table_headers.email', 'Email'),
                      t('settings_organization.player_accounts.table_headers.tag', 'Tag'),
                    ]}
                    rows={tableRowData}
                  />
                )}
              </div>
              <div className={styles.buttonContainer}>
                {!hasCSVData && (!limitPlayers || updatedPlayerCount < maxPlayers) && showAllPlayers && (
                  <AddEntityFieldButton
                    onClick={() => {
                      form.mutators.push('players')
                      setUpdatedPlayerCount((prev) => prev + 1)
                    }}
                    disabled={submitting}
                  >
                    {t('settings_organization.players.add_players.button_text.add_player', 'Add player')}
                  </AddEntityFieldButton>
                )}
                {!showAllPlayers && (
                  <ShowAllMembersButton onClick={() => setShowAllPlayers(true)}>
                    {t('settings_organization.players.add_players.button_text.view_all', 'View all')}
                  </ShowAllMembersButton>
                )}
                {limitPlayers && updatedPlayerCount >= maxPlayers && (
                  <AddMemberError>
                    {t(
                      'settings_organization.players.add_players.errors.add_player_button_error',
                      'Remove players or upgrade your licence to add more.',
                    )}
                  </AddMemberError>
                )}
              </div>
            </div>
            <div className={styles.footer}>
              <Button variant='outline-normal' onClick={onClose} disabled={submitting}>
                {t('settings_organization.players.add_players.button_text.cancel', 'Cancel')}
              </Button>
              <div className={styles.footerRight}>
                {limitPlayers && (
                  <div className={classNames(updatedPlayerCount > maxPlayers && styles.errorRed)}>
                    {updatedPlayerCount}/{maxPlayers}
                    {t('settings_organization.players.add_players.player_count', 'players')}
                  </div>
                )}
                <Button
                  type='submit'
                  disabled={
                    submitting ||
                    (limitPlayers && updatedPlayerCount > maxPlayers) ||
                    invalid ||
                    (hasCSVData && !allowSubmitting)
                  }
                >
                  {t('settings_organization.players.add_players.button_text.preview_and_send', 'Preview and send')}
                </Button>
              </div>
            </div>
          </form>
        )}
      </Form>
    </>
  )
}
